import { getKnownPhrases, getLearningPhrases, searchPhrases } from "@/api/Api";
import { callWithAuth } from "@/api/Api";
import { UserPhrase } from "@shared_types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";

type UseUserPhrasesProps = {
  viewMode: "known" | "learning";
  searchQuery: string;
  pageSize: number;
  phraseQueryKeys: {
    knownPhrasesKey: string;
    learningPhrasesKey: string;
  };
  isSearching: boolean;
};

// Number of items to render initially, regardless of how many are in cache
const INITIAL_DISPLAY_LIMIT = 30;

export const useUserPhrases = ({
  viewMode,
  searchQuery,
  pageSize,
  phraseQueryKeys,
  isSearching,
}: UseUserPhrasesProps) => {
  // Track how many items should be displayed
  const [displayLimit, setDisplayLimit] = useState(INITIAL_DISPLAY_LIMIT);

  // Reset the display limit when switching views or changing search queries
  useEffect(() => {
    setDisplayLimit(INITIAL_DISPLAY_LIMIT);
  }, [viewMode, searchQuery]);

  // Fetch known phrases with pagination
  const knownPhrasesQuery = useInfiniteQuery({
    queryKey: [phraseQueryKeys.knownPhrasesKey],
    queryFn: ({ pageParam = 1 }) => {
      return callWithAuth(getKnownPhrases, {
        page: pageParam,
        limit: pageSize,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage !== 0 ? lastPage.nextPage : undefined,
    initialPageParam: 1,
  });

  // Fetch learning phrases with pagination
  const learningPhrasesQuery = useInfiniteQuery({
    queryKey: [phraseQueryKeys.learningPhrasesKey],
    queryFn: ({ pageParam = 1 }) => {
      return callWithAuth(getLearningPhrases, {
        page: pageParam,
        limit: pageSize,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage !== 0 ? lastPage.nextPage : undefined,
    initialPageParam: 1,
  });

  // Search query for server-side search
  const searchPhrasesQuery = useInfiniteQuery({
    queryKey: ["searchPhrases", viewMode, searchQuery],
    queryFn: ({ pageParam = 1 }) => {
      return callWithAuth(searchPhrases, {
        status: viewMode,
        query: searchQuery,
        page: pageParam,
        limit: pageSize,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage !== 0 ? lastPage.nextPage : undefined,
    initialPageParam: 1,
    enabled: isSearching,
  });

  // Get all phrases from query data
  const knownPhrases =
    knownPhrasesQuery.data?.pages.flatMap((page) => page.phrases) || [];
  // Get all learning phrases from query data
  const learningPhrases =
    learningPhrasesQuery.data?.pages.flatMap((page) => page.phrases) || [];
  // Get search results
  const serverSearchResult =
    searchPhrasesQuery.data?.pages.flatMap((page) => page.phrases) || [];

  // Determine all available data
  let allPhrases: UserPhrase[] = [];
  if (isSearching) {
    allPhrases = serverSearchResult;
  } else if (viewMode === "known") {
    allPhrases = knownPhrases;
  } else if (viewMode === "learning") {
    allPhrases = learningPhrases;
  } else {
    console.error("Invalid view mode");
    allPhrases = [];
  }

  // Limit the number of items actually returned for rendering
  const activeViewPhrases = allPhrases.slice(0, displayLimit);

  // Determine if we have more items to show from cache
  const hasMoreToShow = displayLimit < allPhrases.length;

  const activeQuery = isSearching
    ? searchPhrasesQuery
    : viewMode === "known"
    ? knownPhrasesQuery
    : learningPhrasesQuery;

  // Function to load more items from cache or fetch more if needed
  const loadMore = () => {
    if (displayLimit < allPhrases.length) {
      // We have more cached items, just increase the display limit
      setDisplayLimit((prev) => prev + INITIAL_DISPLAY_LIMIT);
    } else if (activeQuery.hasNextPage && !activeQuery.isFetchingNextPage) {
      // We need to fetch more from the server
      activeQuery.fetchNextPage().then(() => {
        // After fetching, increase display limit to show new items
        setDisplayLimit((prev) => prev + INITIAL_DISPLAY_LIMIT);
      });
    }
  };

  return {
    activeQuery,
    activeViewPhrases,
    fetchNextPage: loadMore,
    hasMoreToShow,
    totalItemsAvailable: allPhrases.length,
    refetchActiveView: () => {
      if (isSearching) {
        searchPhrasesQuery.refetch();
      } else if (viewMode === "known") {
        knownPhrasesQuery.refetch();
      } else {
        learningPhrasesQuery.refetch();
      }
      setDisplayLimit(INITIAL_DISPLAY_LIMIT);
    },
    refetchAllQueries: () => {
      knownPhrasesQuery.refetch();
      learningPhrasesQuery.refetch();
      if (isSearching) {
        searchPhrasesQuery.refetch();
      }
      setDisplayLimit(INITIAL_DISPLAY_LIMIT);
    },
    isLoading:
      (viewMode === "known" && knownPhrasesQuery.isLoading) ||
      (viewMode === "learning" && learningPhrasesQuery.isLoading) ||
      (isSearching && searchPhrasesQuery.isLoading),
    isFetchingNextPage:
      (viewMode === "known" && knownPhrasesQuery.isFetchingNextPage) ||
      (viewMode === "learning" && learningPhrasesQuery.isFetchingNextPage) ||
      (isSearching && searchPhrasesQuery.isFetchingNextPage) ||
      (hasMoreToShow && displayLimit < allPhrases.length),
  };
};
